import React, { Fragment } from 'react';
import { format } from 'date-fns';

import StatusBadge from '@dashboard/components/StatusBadge';
import Spinner from '@dashboard/components/Spinner';

import { formatPrice } from '@dashboard/services/formatting';

import Button from '@dashboard/components/Button';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import useSellerQueries from '@dashboard/hooks/queries/useSellerQueries';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import BottomScrollListener from '@dashboard/components/BottomScrollListener';

const Invoices = () => {
  const { sellerId } = useAuth();
  const { getInvoices } = useSellerQueries();

  // fetch invoices with infinite react-query
  const { data, fetchNextPage, isFetchingNextPage, isError, isLoading } = useInfiniteQuery({
    queryKey: ['invoices', sellerId],
    queryFn: async ({ pageParam }) =>
      await getInvoices(sellerId, {
        limit: 10,
        skip: pageParam * 10,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;

      return allPages.length;
    },
  });

  if (isLoading) {
    return <Spinner />;
  }

  if (isError) {
    return <div>Failed to load invoices. Please try again.</div>;
  }

  const isEmpty = !data?.pages?.[0]?.data?.length;

  if (isEmpty) {
    return <div>No invoices yet</div>;
  }

  return (
    <table className="text-right w-full p-0">
      <thead>
        <tr>
          <th className="text-left">Date</th>
          <th>Amount</th>
          <th>Status</th>
          <th>Type</th>
          <th>Download</th>
        </tr>
      </thead>

      <tbody>
        <BottomScrollListener onBottom={fetchNextPage} as={Fragment}>
          {data?.pages?.map(data =>
            data?.data.map(invoice => (
              <tr key={invoice.id}>
                <td className="text-left pt-2 pb-2 pr-4">
                  {format(new Date(invoice.created * 1000), 'd MMM, yyyy')}
                </td>
                <td className="pt-2 pb-2 pl-4">
                  {formatPrice({
                    amount: invoice.total,
                    currency: invoice.currency,
                  })}
                </td>
                <td className="pt-2 pb-2 pl-4">
                  <StatusBadge status={invoice.status}>{invoice.status}</StatusBadge>
                </td>
                <td className="pt-2 pb-2 pl-4">
                  {invoice.lines?.data?.some(item =>
                    item?.description?.toLowerCase().includes('overage fee'),
                  )
                    ? 'Overage fee'
                    : 'Subscription'}
                </td>
                <td className="pt-2 pb-2 pl-4">
                  <Button
                    href={invoice.invoice_pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    appearance="light"
                    size="small"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </td>
              </tr>
            )),
          )}
        </BottomScrollListener>

        {isFetchingNextPage && <Spinner className="m-4" />}
      </tbody>
    </table>
  );
};

export default Invoices;
